import { invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
import {
  classifyDictationError,
  dictationOffHint,
  isVoicePermissionError,
} from "./dictation-error-guidance";
import { clearDictationAttention } from "./dictation-attention";
import { abandonProvisionalVoiceNewChatRouting } from "./voice-new-chat-routing";
import { isContinuousCaptureAlive } from "./continuous-voice-store";
import { setVoiceIndicatorSuppressed } from "./voice-indicator-visibility";
import { ensureVoicePermissionsForFeatureUse } from "./voice-permission-preflight";
import { getWindowRoleSync } from "./use-window-role";
import { track } from "./observability";
import type { VoiceCaptureStatePayload } from "./continuous-voice-native-session";
import type { DictationErrorKind } from "./dictation-error-guidance";

type NativeDictationState = {
  status: "idle" | "recording" | "processing" | "error";
  sessionId: string | null;
  lastError?: string;
  errorKind?: DictationErrorKind;
  unavailable: boolean;
  volume: number;
  deadMic: boolean;
};

type NativeLifecycleOptions = {
  getState: () => NativeDictationState;
  setState: (state: Partial<NativeDictationState>) => void;
  setCancelling: (value: boolean) => void;
  cancelRequest: () => void;
  clearProcessingWatchdog: () => void;
  applyMirrorVisualState: (apply: () => void) => void;
  resetVolumeAndDeadMic: () => void;
  scheduleDeadMicCheck: () => void;
  scheduleErrorClear: () => void;
  debug: (message: string) => void;
};

// Native RMS frames arrive far faster than the volume meter (or the mirror
// bridge) needs: without throttling every frame triggered a store set +
// cross-window emit, which piles up while the webview is backgrounded and
// then reconciles in one burst on foreground.
const VOLUME_EMIT_MIN_INTERVAL_MS = 120;
const VOLUME_EMIT_MIN_DELTA = 0.005;
let lastVolumeEmittedAt = 0;
let lastVolumeEmittedValue = -1;
/** The one-shot recording whose microphone is reopening, between its
 *  `restarting` and `restarted`. Keyed by session so it cannot outlive it. */
let restartingSessionId: string | null = null;

export function attachDictationNativeStateLifecycle(
  options: NativeLifecycleOptions,
): void {
  void listen<{
    state: "ready" | "recording" | "stopped" | "error";
    code?: string;
    message?: string;
  }>("voice-state", ({ payload }) => {
    const isOwner = getWindowRoleSync() === "owner";
    const { state, code, message } = payload;
    options.debug(
      `voice-state state=${state} code=${code ?? ""} msg=${message ?? ""}`,
    );
    if (state === "recording" && !isOwner) {
      options.setCancelling(false);
      options.clearProcessingWatchdog();
      options.applyMirrorVisualState(() => {
        setVoiceIndicatorSuppressed(false);
        options.setState({
          status: "recording",
          lastError: undefined,
          errorKind: undefined,
          volume: 0,
          deadMic: false,
        });
      });
      return;
    }
    if (state === "error" && isOwner) {
      if (code === "continuous_capture_busy" && isContinuousCaptureAlive()) {
        // The sidecar refused a stray one-shot start while continuous
        // capture owns the engine. Live listening is unaffected — applying
        // this error would flip the shared recording status into an error
        // and drop the voice-active key tap mid-capture.
        options.debug("voice-state continuous_capture_busy ignored (live)");
        return;
      }
      const isPermanent =
        code === "sidecar_crashed_repeatedly" ||
        message?.includes("crashed repeatedly");
      const errorKind = classifyDictationError(code);
      if (
        !isPermanent &&
        errorKind !== "dictation_off" &&
        options.getState().status === "idle"
      ) {
        options.debug(
          `voice-state transient idle error ignored code=${code ?? ""} msg=${message ?? ""}`,
        );
        return;
      }
      options.setCancelling(false);
      options.cancelRequest();
      abandonProvisionalVoiceNewChatRouting("native_error");
      options.clearProcessingWatchdog();
      clearDictationAttention();
      void invoke("set_voice_active", { active: false }).catch(() => {});
      setVoiceIndicatorSuppressed(false);
      options.setState({
        status: "error",
        lastError:
          errorKind === "dictation_off"
            ? dictationOffHint()
            : (message ?? code ?? "voice error"),
        errorKind,
        unavailable: isPermanent ? true : options.getState().unavailable,
      });
      track("desktop_voice_dictation_error", {
        error_code: code ?? "unknown",
        message: message?.slice(0, 200) ?? "",
        is_permanent: !!isPermanent,
        error_kind: errorKind,
        phase: "sidecar",
      });
      options.resetVolumeAndDeadMic();
      if (isVoicePermissionError(code, message)) {
        void ensureVoicePermissionsForFeatureUse();
      }
      if (errorKind !== "dictation_off") options.scheduleErrorClear();
      return;
    }
    if (state !== "stopped") return;
    options.clearProcessingWatchdog();
    if (isOwner) {
      options.resetVolumeAndDeadMic();
    } else {
      options.applyMirrorVisualState(() => {
        options.resetVolumeAndDeadMic();
        if (options.getState().status === "recording") {
          options.setState({ status: "processing" });
        }
      });
    }
  });

  // A one-shot capture reopening its microphone sends no audio until it is
  // back - over three seconds for a Bluetooth profile switch. The dead-mic
  // check is off for the gap, so it is not read as a silent microphone, and
  // starts afresh once the capture is back.
  void listen<VoiceCaptureStatePayload>(
    "voice-capture-state",
    ({ payload }) => {
      const state = options.getState();
      if (
        payload.kind !== "one-shot" ||
        getWindowRoleSync() !== "owner" ||
        state.status !== "recording" ||
        payload.session_id !== state.sessionId
      )
        return;
      options.debug(
        `voice-capture-state ${payload.value} reason=${payload.reason} restart=${payload.restart}`,
      );
      if (payload.value === "restarting") {
        restartingSessionId = state.sessionId;
        options.resetVolumeAndDeadMic();
      } else {
        restartingSessionId = null;
        options.scheduleDeadMicCheck();
      }
    },
  );

  void listen<Record<string, unknown>>("voice-diagnostic", ({ payload }) => {
    const event = typeof payload.event === "string" ? payload.event : "";
    if (
      event !== "engine_selected" &&
      event !== "engine_fallback" &&
      event !== "speech_analyzer_unavailable" &&
      event !== "sf_speech_rotation_requested"
    )
      return;
    const value = (key: string) =>
      typeof payload[key] === "string" ? payload[key] : "";
    options.debug(
      `voice-diagnostic event=${event} engine=${value("engine")} reason=${value("reason")} to=${value("to")} asset_status=${value("asset_status")}`,
    );
  });

  void listen<{ rms: number }>("voice-volume", ({ payload }) => {
    if (options.getState().status !== "recording") return;
    const rms = payload.rms ?? 0;
    const now = Date.now();
    const audible = rms > 0.02;
    if (
      !audible &&
      Math.abs(rms - lastVolumeEmittedValue) < VOLUME_EMIT_MIN_DELTA &&
      now - lastVolumeEmittedAt < VOLUME_EMIT_MIN_INTERVAL_MS
    ) {
      return;
    }
    lastVolumeEmittedAt = now;
    lastVolumeEmittedValue = rms;
    const applyVolume = () => {
      options.setState({ volume: rms });
      if (audible) options.setState({ deadMic: false });
    };
    if (getWindowRoleSync() === "owner") {
      applyVolume();
      if (
        restartingSessionId === null ||
        restartingSessionId !== options.getState().sessionId
      )
        options.scheduleDeadMicCheck();
    } else {
      options.applyMirrorVisualState(applyVolume);
    }
  });
}
